import React, { useMemo, useState } from 'react';
import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    pointerWithin,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
    Button,
    Collapse,
    Col,
    Empty,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
    Switch,
    Tag,
    Typography,
} from 'antd';
import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';

import ModuleConfigEditor from '../Configuration/ModuleConfigEditor';
import type { MissionCommandDraft } from './missionModels';
import {
    MISSION_COMMAND_KIND_OPTIONS,
    MISSION_COMMAND_NAME_OPTIONS,
} from './missionModels';
import type { SelectProps } from 'antd';
import {
    getCommandDisplayName,
    getProtocolFilteredCommands,
    resolveCommand,
} from './missionCommandCatalogue';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Re-assign dense sequential integers (0, 1, 2 …) to all commands while
 *  preserving their relative group order. */
export function normalizeExecutionGroups(commands: MissionCommandDraft[]): MissionCommandDraft[] {
    const uniqueGroups = [...new Set(commands.map((c) => c.executionGroup ?? 0))].sort((a, b) => a - b);
    const groupMap = new Map(uniqueGroups.map((g, i) => [g, i]));
    return commands.map((c) => ({ ...c, executionGroup: groupMap.get(c.executionGroup ?? 0) ?? 0 }));
}

type CommandRow = {
    group: number;
    commands: MissionCommandDraft[];
    isLocked: boolean;
};

export type ModuleCommandSpec = {
    key?: string;
    name?: string;
    description?: string | null;
    kind?: string;
    parameterSchema?: Record<string, any> | null;
    defaultParameters?: Record<string, any> | null;
    executionMapping?: {
        protocol?: string | null;
        mavlinkCommandCode?: number | null;
        mavlinkCommandName?: string | null;
        paramMapping?: Record<string, number | string> | null;
        endpoint?: string | null;
        method?: string | null;
    } | null;
};

export type CommandModuleConfig = {
    moduleKey: string;
    instanceKey?: string | null;
    label: string;
    moduleDefinitionKey?: string | null;
    moduleDefinitionId?: number | null;
    status?: string | null;
    triggerMethod?: string | null;
    schema?: Record<string, any> | null;
    defaultConfig?: Record<string, any> | null;
    commandSpecs?: ModuleCommandSpec[] | null;
};

type ModuleCommandOption = {
    value: string;
    label: string;
    description?: string | null;
    moduleKey: string;
    instanceKey?: string | null;
    moduleDefinitionKey?: string | null;
    moduleDefinitionId?: number | null;
    commandSpecKey: string;
    commandName: string;
    commandCode: number | null;
    triggerMethod: string;
    executionMapping?: ModuleCommandSpec['executionMapping'];
    defaultParameters?: Record<string, any> | null;
    defaultConfig?: Record<string, any> | null;
};

const cloneJsonObject = (value: unknown): Record<string, any> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {};
    }
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return { ...(value as Record<string, any>) };
    }
};

const buildModuleCommandValue = (moduleKey: string, commandSpecKey: string): string => (
    `module:${encodeURIComponent(moduleKey)}:${encodeURIComponent(commandSpecKey)}`
);

const getCommandSpecKey = (spec: ModuleCommandSpec, index: number): string => (
    String(spec.key || `command_${index + 1}`).trim() || `command_${index + 1}`
);

const getCommandSpecLabel = (spec: ModuleCommandSpec, commandSpecKey: string): string => (
    String(spec.name || spec.description || commandSpecKey).trim() || commandSpecKey
);

const getModuleCommandCode = (spec?: ModuleCommandSpec | null): number | null => {
    const rawCode = spec?.executionMapping?.mavlinkCommandCode;
    const code = Number(rawCode);
    return Number.isInteger(code) ? code : null;
};

const isModuleAvailableForManualCommand = (config: CommandModuleConfig): boolean => {
    const status = String(config.status || 'installed').toLowerCase();
    return status !== 'disabled' && status !== 'removed';
};

const isModuleSpecCompatibleWithProtocol = (spec: ModuleCommandSpec, protocol: string): boolean => {
    const proto = (protocol || 'oscar').toLowerCase();
    if (proto !== 'mavlink' && proto !== 'ardupilot') {
        return true;
    }

    return (
        spec.kind === 'mavlink'
        || getModuleCommandCode(spec) !== null
        || Boolean(spec.executionMapping?.mavlinkCommandName)
    );
};

const buildModuleCommandOptions = (
    moduleConfigsByKey: Record<string, CommandModuleConfig> | undefined,
    protocol: string
): ModuleCommandOption[] => {
    if (!moduleConfigsByKey) {
        return [];
    }

    const proto = (protocol || 'oscar').toLowerCase();
    const isMavLike = proto === 'mavlink' || proto === 'ardupilot';
    const seenModuleKeys = new Set<string>();
    const options: ModuleCommandOption[] = [];

    Object.values(moduleConfigsByKey).forEach((config) => {
        if (!config?.moduleKey || seenModuleKeys.has(config.moduleKey) || !isModuleAvailableForManualCommand(config)) {
            return;
        }
        seenModuleKeys.add(config.moduleKey);

        const commandName = config.moduleDefinitionKey || config.moduleKey;
        const compatibleSpecs = (config.commandSpecs || [])
            .filter((spec) => spec && typeof spec === 'object')
            .filter((spec) => isModuleSpecCompatibleWithProtocol(spec, proto));

        compatibleSpecs.forEach((spec, index) => {
            const commandSpecKey = getCommandSpecKey(spec, index);
            const specLabel = getCommandSpecLabel(spec, commandSpecKey);
            options.push({
                value: buildModuleCommandValue(config.moduleKey, commandSpecKey),
                label: `${config.label} - ${specLabel}`,
                description: spec.description || null,
                moduleKey: config.moduleKey,
                instanceKey: config.instanceKey || null,
                moduleDefinitionKey: config.moduleDefinitionKey,
                moduleDefinitionId: config.moduleDefinitionId,
                commandSpecKey,
                commandName: commandSpecKey,
                commandCode: getModuleCommandCode(spec),
                triggerMethod: config.triggerMethod || 'auto',
                executionMapping: spec.executionMapping || null,
                defaultParameters: spec.defaultParameters || null,
                defaultConfig: config.defaultConfig || null,
            });
        });

        if (!compatibleSpecs.length && !isMavLike) {
            options.push({
                value: buildModuleCommandValue(config.moduleKey, 'default'),
                label: `${config.label} - Trigger`,
                description: null,
                moduleKey: config.moduleKey,
                instanceKey: config.instanceKey || null,
                moduleDefinitionKey: config.moduleDefinitionKey,
                moduleDefinitionId: config.moduleDefinitionId,
                commandSpecKey: 'default',
                commandName,
                commandCode: null,
                triggerMethod: config.triggerMethod || 'auto',
                executionMapping: null,
                defaultParameters: null,
                defaultConfig: config.defaultConfig || null,
            });
        }
    });

    return options;
};

const findModuleCommandOption = (
    command: MissionCommandDraft,
    moduleCommandOptions: ModuleCommandOption[]
): ModuleCommandOption | null => {
    if (command.source !== 'device_module') {
        return null;
    }

    const moduleKey = String(command.params?.instanceKey || command.params?.moduleKey || '').trim();
    const moduleDefinitionKey = String(command.params?.moduleKey || '').trim();
    const commandSpecKey = String(command.params?.commandSpecKey || '').trim();
    return (
        moduleCommandOptions.find((option) => (
            (option.moduleKey === moduleKey || option.instanceKey === moduleKey || option.moduleDefinitionKey === moduleDefinitionKey)
            && (!commandSpecKey || option.commandSpecKey === commandSpecKey)
        ))
        || moduleCommandOptions.find((option) => (
            option.commandName === command.commandName
            && (!commandSpecKey || option.commandSpecKey === commandSpecKey)
        ))
        || null
    );
};

const getDraftCommandDisplayName = (
    command: MissionCommandDraft,
    moduleConfigsByKey?: Record<string, CommandModuleConfig>,
    moduleCommandOptions: ModuleCommandOption[] = []
): string => {
    const moduleOption = findModuleCommandOption(command, moduleCommandOptions);
    if (moduleOption) {
        return moduleOption.label;
    }

    if (command.source === 'device_module') {
        const moduleKey = String(command.params?.moduleKey || command.commandName || '');
        const moduleConfig = moduleConfigsByKey?.[moduleKey] || moduleConfigsByKey?.[command.commandName];
        return command.label?.trim() || moduleConfig?.label || moduleKey || command.commandName;
    }

    return getCommandDisplayName(command.commandName);
};

const getDraftCommandDescription = (
    command: MissionCommandDraft,
    moduleCommandOptions: ModuleCommandOption[] = []
): string | null => {
    const moduleOption = findModuleCommandOption(command, moduleCommandOptions);
    if (moduleOption?.description) {
        return moduleOption.description;
    }
    return resolveCommand(command.commandName)?.description || null;
};

function buildRows(pointCommands: MissionCommandDraft[]): CommandRow[] {
    if (!pointCommands.length) return [];

    const groupMap = new Map<number, MissionCommandDraft[]>();
    pointCommands.forEach((cmd) => {
        const g = cmd.executionGroup ?? 0;
        if (!groupMap.has(g)) groupMap.set(g, []);
        groupMap.get(g)!.push(cmd);
    });

    const sortedGroups = [...groupMap.keys()].sort((a, b) => a - b);

    return sortedGroups.map((g) => ({
        group: g,
        commands: groupMap.get(g)!,
        isLocked: false,
    }));
}

// ---------------------------------------------------------------------------
// Droppable row zone (drop onto a row = make parallel)
// ---------------------------------------------------------------------------
function DroppableRow({
    group,
    isOver,
    isLocked,
    children,
}: {
    group: number;
    isOver: boolean;
    isLocked: boolean;
    children: React.ReactNode;
}) {
    const { setNodeRef } = useDroppable({ id: `row-${group}`, disabled: isLocked });
    return (
        <div ref={setNodeRef} className={`cmd-grid-row-cards${isOver && !isLocked ? ' cmd-grid-row-cards-over' : ''}`}>
            {children}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Droppable gap zone between rows (drop = new sequential step)
// ---------------------------------------------------------------------------
function DroppableGap({ afterGroup, isOver }: { afterGroup: number; isOver: boolean }) {
    const { setNodeRef } = useDroppable({ id: `gap-${afterGroup}` });
    return (
        <div ref={setNodeRef} className={`cmd-grid-gap${isOver ? ' cmd-grid-gap-over' : ''}`}>
            <div className="cmd-grid-gap-line" />
            {isOver && <span className="cmd-grid-gap-label">Insert new step here</span>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Draggable command card
// ---------------------------------------------------------------------------
function DraggableCommandCard({
    command,
    isSelected,
    isLocked,
    dragDisabled,
    moduleConfigsByKey,
    moduleCommandOptions,
    onSelect,
    onRemove,
}: {
    command: MissionCommandDraft;
    isSelected: boolean;
    isLocked: boolean;
    dragDisabled?: boolean;
    moduleConfigsByKey?: Record<string, CommandModuleConfig>;
    moduleCommandOptions?: ModuleCommandOption[];
    onSelect: () => void;
    onRemove: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `cmd-${command.key}`,
        disabled: isLocked || dragDisabled,
        data: { commandKey: command.key, executionGroup: command.executionGroup ?? 0 },
    });

    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.4 : 1,
    };
    const displayName = getDraftCommandDisplayName(command, moduleConfigsByKey, moduleCommandOptions);
    const description = getDraftCommandDescription(command, moduleCommandOptions);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={[
                'cmd-grid-card',
                isSelected ? 'cmd-grid-card-selected' : '',
                isDragging ? 'cmd-grid-card-dragging' : '',
            ]
                .filter(Boolean)
                .join(' ')}
            onClick={onSelect}
        >
            {!isLocked && !dragDisabled && (
                <div className="cmd-grid-card-handle" {...listeners} {...attributes} title="Drag to reorder">
                    <HolderOutlined />
                </div>
            )}
            <div className="cmd-grid-card-content">
                <Space wrap size={4}>
                    <Tag color={command.isPrimary ? 'blue' : command.kind === 'do' ? 'orange' : 'default'} style={{ margin: 0 }}>
                        {command.isPrimary ? 'Primary' : command.kind.toUpperCase()}
                    </Tag>
                    <Tag style={{ margin: 0 }}>{displayName}</Tag>
                </Space>
                <Text className="cmd-grid-card-label" strong ellipsis>
                    {command.label?.trim() || displayName}
                </Text>
                <Text type="secondary" className="cmd-grid-card-desc" ellipsis>
                    {description ||
                        (command.source === 'generated' ? 'Auto-generated' :
                         command.source === 'device_module' ? 'Device module' :
                         command.source === 'imported' ? 'Imported' : 'Manual')}
                </Text>
            </div>
            {!isLocked && !command.isPrimary && (
                <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    className="cmd-grid-card-delete"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    title="Remove command"
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Command detail form (shown below grid when a card is selected)
// ---------------------------------------------------------------------------
function CommandDetailForm({
    command,
    commandProtocol,
    moduleConfigsByKey,
    moduleCommandOptions,
    onChange,
    onRemove,
}: {
    command: MissionCommandDraft;
    commandProtocol: string;
    moduleConfigsByKey?: Record<string, CommandModuleConfig>;
    moduleCommandOptions?: ModuleCommandOption[];
    onChange: (updates: Partial<MissionCommandDraft>) => void;
    onRemove: () => void;
}) {
    const proto = (commandProtocol || 'oscar').toLowerCase();
    const isMavLike = proto === 'mavlink' || proto === 'ardupilot';
    const isCustom = proto === 'custom';
    const showTechnicalFields = isMavLike || isCustom;
    const isModuleCommand = command.source === 'device_module' && command.kind === 'do';
    const showParamsEditor = isCustom;
    const moduleKey = String(command.params?.moduleKey || command.commandName || '');
    const moduleConfig = moduleKey ? moduleConfigsByKey?.[moduleKey] : undefined;
    const moduleOptions = moduleCommandOptions || [];
    const selectedModuleOption = findModuleCommandOption(command, moduleOptions);

    const kindOptions = isMavLike
        ? MISSION_COMMAND_KIND_OPTIONS.filter((option) => option.value !== 'custom')
        : MISSION_COMMAND_KIND_OPTIONS;

    // Build command name options filtered and labelled by protocol
    const standardNameOptions: NonNullable<SelectProps<string>['options']> = getProtocolFilteredCommands(proto, { complexity: 'advanced' })
        .filter((cmd) => !command.kind || cmd.kind === command.kind || command.kind === 'custom')
        .map((cmd) => ({
            value: cmd.id,
            label: isMavLike || isCustom
                ? (cmd.mavlinkCode !== null ? `${cmd.displayName} (${cmd.mavlinkCode})` : cmd.displayName)
                : cmd.displayName,
        }));
    const moduleNameOptions: NonNullable<SelectProps<string>['options']> = command.kind === 'do'
        ? moduleOptions.map((option) => ({ value: option.value, label: option.label }))
        : [];
    const nameOptions: NonNullable<SelectProps<string>['options']> = moduleNameOptions.length > 0 && command.kind === 'do'
        ? [
            { label: 'Device modules', options: moduleNameOptions },
            { label: 'Standard DO commands', options: standardNameOptions },
        ]
        : standardNameOptions;
    const commandSelectValue = selectedModuleOption?.value || command.commandName;
    const commandDescription = selectedModuleOption?.description || resolveCommand(command.commandName)?.description || null;

    // Params JSON editor state (for 'custom' protocol)
    const [paramsText, setParamsText] = React.useState<string>(() => {
        try { return JSON.stringify(command.params || {}, null, 2); } catch { return '{}'; }
    });

    React.useEffect(() => {
        try {
            setParamsText(JSON.stringify(command.params || {}, null, 2));
        } catch {
            setParamsText('{}');
        }
    }, [command.key, command.params]);

    return (
        <div className="cmd-grid-detail">
            <Text className="operations-editor-field-label" style={{ display: 'block', marginBottom: 8 }}>
                Command detail
            </Text>
            <Row gutter={[12, 12]}>
                <Col xs={24} sm={12}>
                    <div className="operations-editor-field">
                        <Text className="operations-editor-field-label">Kind</Text>
                        <Select
                            value={command.kind}
                            options={kindOptions}
                            style={{ width: '100%' }}
                            onChange={(value) => {
                                const nextCommand = getProtocolFilteredCommands(proto, { complexity: 'advanced' })
                                    .find((cmd) => cmd.kind === value);
                                const nextOptions = MISSION_COMMAND_NAME_OPTIONS[value] || MISSION_COMMAND_NAME_OPTIONS.custom;
                                onChange({
                                    kind: value,
                                    commandName: nextCommand?.id || nextOptions[0]?.value || command.commandName,
                                    commandCode: nextCommand?.mavlinkCode ?? command.commandCode,
                                    source: 'manual',
                                    params: {},
                                });
                            }}
                        />
                    </div>
                </Col>
                <Col xs={24} sm={12}>
                    <div className="operations-editor-field">
                        <Text className="operations-editor-field-label">Label</Text>
                        <Input
                            value={command.label}
                            placeholder="Optional operator label"
                            onChange={(e) => onChange({ label: e.target.value })}
                        />
                    </div>
                </Col>
                <Col xs={24}>
                    <div className="operations-editor-field">
                        <Text className="operations-editor-field-label">Command</Text>
                        <Select
                            value={commandSelectValue}
                            showSearch
                            optionFilterProp="label"
                            style={{ width: '100%' }}
                            options={nameOptions}
                            onChange={(value) => {
                                const moduleOption = moduleOptions.find((option) => option.value === value);
                                if (moduleOption) {
                                    onChange({
                                        kind: 'do',
                                        commandName: moduleOption.commandName,
                                        commandCode: moduleOption.commandCode,
                                        source: 'device_module',
                                        params: {
                                            moduleKey: moduleOption.moduleDefinitionKey || moduleOption.moduleKey,
                                            ...(moduleOption.instanceKey ? { instanceKey: moduleOption.instanceKey } : {}),
                                            commandSpecKey: moduleOption.commandSpecKey,
                                            triggerMethod: moduleOption.triggerMethod,
                                            config: (
                                                command.params?.moduleKey === (moduleOption.moduleDefinitionKey || moduleOption.moduleKey)
                                                || command.params?.instanceKey === moduleOption.instanceKey
                                            ) && command.params?.config
                                                ? cloneJsonObject(command.params.config)
                                                : cloneJsonObject(moduleOption.defaultConfig),
                                            commandParameters: cloneJsonObject(moduleOption.defaultParameters),
                                            executionMapping: moduleOption.executionMapping
                                                ? cloneJsonObject(moduleOption.executionMapping)
                                                : null,
                                            ...(moduleOption.moduleDefinitionId ? { moduleDefinitionId: moduleOption.moduleDefinitionId } : {}),
                                        },
                                    });
                                    return;
                                }

                                const cmd = resolveCommand(value);
                                onChange({
                                    commandName: value,
                                    commandCode: cmd?.mavlinkCode ?? command.commandCode,
                                    source: 'manual',
                                    params: {},
                                });
                            }}
                        />
                        {commandDescription && (
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                {commandDescription}
                            </Text>
                        )}
                    </div>
                </Col>

                {/* Technical MAVLink fields — hidden for oscar protocol */}
                {showTechnicalFields && (
                    <>
                        <Col xs={24} sm={8}>
                            <div className="operations-editor-field">
                                <Text className="operations-editor-field-label">Command code</Text>
                                <InputNumber
                                    value={command.commandCode ?? undefined}
                                    style={{ width: '100%' }}
                                    onChange={(v) => onChange({ commandCode: v ?? null })}
                                />
                            </div>
                        </Col>
                        <Col xs={24} sm={8}>
                            <div className="operations-editor-field">
                                <Text className="operations-editor-field-label">Frame</Text>
                                <InputNumber
                                    value={command.frame ?? undefined}
                                    style={{ width: '100%' }}
                                    onChange={(v) => onChange({ frame: v ?? null })}
                                />
                            </div>
                        </Col>
                        <Col xs={24} sm={8}>
                            <div className="operations-editor-field">
                                <Text className="operations-editor-field-label">Altitude</Text>
                                <InputNumber
                                    value={command.altitude ?? undefined}
                                    style={{ width: '100%' }}
                                    onChange={(v) => onChange({ altitude: v ?? null })}
                                />
                            </div>
                        </Col>
                    </>
                )}

                {isModuleCommand && (
                    <Col xs={24}>
                        <Collapse
                            bordered={false}
                            defaultActiveKey={['settings']}
                            items={[
                                {
                                    key: 'settings',
                                    label: (
                                        <Space wrap>
                                            <Text strong>{moduleConfig?.label || command.label || moduleKey || 'Module settings'}</Text>
                                            {moduleConfig?.moduleDefinitionKey || moduleKey ? (
                                                <Tag color="blue">{moduleConfig?.moduleDefinitionKey || moduleKey}</Tag>
                                            ) : null}
                                            <Tag>{command.params?.triggerMethod || 'auto'}</Tag>
                                        </Space>
                                    ),
                                    children: (
                                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                            <Row gutter={[12, 12]}>
                                                <Col xs={24} sm={12}>
                                                    <div className="operations-editor-field">
                                                        <Text className="operations-editor-field-label">Trigger</Text>
                                                        <Input
                                                            value={command.params?.triggerMethod || 'auto'}
                                                            onChange={(event) => onChange({
                                                                params: {
                                                                    ...(command.params || {}),
                                                                    triggerMethod: event.target.value || 'auto',
                                                                },
                                                            })}
                                                        />
                                                    </div>
                                                </Col>
                                                <Col xs={24} sm={12}>
                                                    <div className="operations-editor-field">
                                                        <Text className="operations-editor-field-label">Module key</Text>
                                                        <Input value={moduleKey} disabled />
                                                    </div>
                                                </Col>
                                            </Row>
                                            {moduleConfig?.schema ? (
                                                <ModuleConfigEditor
                                                    schema={moduleConfig.schema}
                                                    defaultValue={moduleConfig.defaultConfig || {}}
                                                    value={command.params?.config || {}}
                                                    footerText="These values are stored on this mission command and are sent to the robot with this DO."
                                                    onChange={(config) => onChange({
                                                        params: {
                                                            ...(command.params || {}),
                                                            moduleKey,
                                                            triggerMethod: command.params?.triggerMethod || 'auto',
                                                            config,
                                                            ...(moduleConfig.moduleDefinitionId ? { moduleDefinitionId: moduleConfig.moduleDefinitionId } : {}),
                                                        },
                                                    })}
                                                />
                                            ) : (
                                                <Text type="secondary">No structured configuration schema is available for this module.</Text>
                                            )}
                                        </Space>
                                    ),
                                },
                            ]}
                        />
                    </Col>
                )}

                {/* Raw params editor for custom commands. Device-module commands use the structured editor above. */}
                {showParamsEditor && (
                    <Col xs={24}>
                        <div className="operations-editor-field">
                            <Text className="operations-editor-field-label">Params (JSON)</Text>
                            <Input.TextArea
                                rows={4}
                                value={paramsText}
                                style={{ fontFamily: 'monospace', fontSize: 12 }}
                                onChange={(e) => {
                                    setParamsText(e.target.value);
                                    try {
                                        const parsed = JSON.parse(e.target.value);
                                        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                                            onChange({ params: parsed });
                                        }
                                    } catch {
                                        // allow invalid JSON while typing
                                    }
                                }}
                            />
                        </div>
                    </Col>
                )}

                <Col xs={24}>
                    <div className="operations-editor-inspector-footer">
                        <Space>
                            <Text className="operations-editor-field-label">Auto continue</Text>
                            <Switch
                                checked={command.autoContinue}
                                onChange={(checked) => onChange({ autoContinue: checked })}
                            />
                        </Space>
                        {command.isPrimary ? (
                            <Text type="secondary">Primary command cannot be removed.</Text>
                        ) : (
                            <Button danger icon={<DeleteOutlined />} onClick={onRemove}>
                                Remove command
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface CommandExecutionGridProps {
    /** All draft commands belonging to the currently selected mission point. */
    pointCommands: MissionCommandDraft[];
    selectedCommandKey: string | null;
    onCommandSelect: (key: string) => void;
    /** Called when DnD changes the executionGroup assignment of commands. */
    onCommandsChange: (updated: MissionCommandDraft[]) => void;
    /** Called when the user wants to add a new sequential step (kind = 'do' | 'condition' | 'custom'). */
    onAddStep: (kind: string) => void;
    /** Called when the user wants to add a command parallel to an existing row. */
    onAddToRow: (kind: string, targetGroup: number) => void;
    onRemoveCommand: (key: string) => void;
    onCommandChange: (key: string, updates: Partial<MissionCommandDraft>) => void;
    /** Protocol from the selected device type — drives form field visibility and command label format. */
    commandProtocol?: string;
    moduleConfigsByKey?: Record<string, CommandModuleConfig>;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const CommandExecutionGrid: React.FC<CommandExecutionGridProps> = ({
    pointCommands,
    selectedCommandKey,
    onCommandSelect,
    onCommandsChange,
    onAddStep,
    onAddToRow,
    onRemoveCommand,
    onCommandChange,
    commandProtocol = 'oscar',
    moduleConfigsByKey,
}) => {
    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [overDroppableId, setOverDroppableId] = useState<string | null>(null);
    const proto = (commandProtocol || 'oscar').toLowerCase();
    const isMavLike = proto === 'mavlink' || proto === 'ardupilot';
    const moduleCommandOptions = useMemo(
        () => buildModuleCommandOptions(moduleConfigsByKey, proto),
        [moduleConfigsByKey, proto]
    );

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    const rows: CommandRow[] = useMemo(() => buildRows(pointCommands), [pointCommands]);

    const draggingCommand = useMemo(() => {
        if (!draggingId) return null;
        const key = draggingId.replace(/^cmd-/, '');
        return pointCommands.find((c) => c.key === key) || null;
    }, [draggingId, pointCommands]);

    const selectedCommand = useMemo(
        () => (selectedCommandKey ? pointCommands.find((c) => c.key === selectedCommandKey) ?? null : null),
        [selectedCommandKey, pointCommands]
    );

    const handleDragStart = (event: DragStartEvent) => {
        if (isMavLike) return;
        setDraggingId(String(event.active.id));
    };

    const handleDragOver = (event: DragOverEvent) => {
        if (isMavLike) return;
        setOverDroppableId(event.over ? String(event.over.id) : null);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setDraggingId(null);
        setOverDroppableId(null);

        if (isMavLike || !event.over) return;

        const cmdKey = String(event.active.id).replace(/^cmd-/, '');
        const overId = String(event.over.id);
        const cmd = pointCommands.find((c) => c.key === cmdKey);
        if (!cmd) return;

        if (overId.startsWith('row-')) {
            const targetGroup = parseInt(overId.replace('row-', ''), 10);
            if (targetGroup === (cmd.executionGroup ?? 0)) return; // no-op
            const updated = normalizeExecutionGroups(
                pointCommands.map((c) => (c.key === cmdKey ? { ...c, executionGroup: targetGroup } : c))
            );
            onCommandsChange(updated);
        } else if (overId.startsWith('gap-')) {
            const afterGroup = parseInt(overId.replace('gap-', ''), 10);
            // Shift all groups > afterGroup up by 1, then set this cmd to afterGroup + 1
            const updated = normalizeExecutionGroups(
                pointCommands.map((c) => {
                    if (c.key === cmdKey) return { ...c, executionGroup: afterGroup + 0.5 };
                    return c;
                })
            );
            onCommandsChange(updated);
        }
    };

    if (!pointCommands.length) {
        return (
            <div className="cmd-grid-empty">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a mission point to manage its commands." />
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
        >
            <div className="cmd-grid">
                <Text type="secondary" className="cmd-grid-hint">
                    {isMavLike ? (
                        <>MAVLink missions execute as a flat ordered command list. Grouping is disabled for GCS-compatible behavior.</>
                    ) : (
                        <>
                            Commands on the <strong>same row</strong> run in <strong>parallel</strong>. Each row is a sequential step.
                            Drag a card onto another row to run them simultaneously, or into a gap to create a new sequential step.
                        </>
                    )}
                </Text>

                <div className="cmd-grid-rows">
                    {rows.map((row, rowIndex) => (
                        <React.Fragment key={`row-frag-${row.group}`}>
                            {/* Gap between rows (except before the very first row) */}
                            {!isMavLike && rowIndex > 0 && (
                                <DroppableGap
                                    afterGroup={rows[rowIndex - 1].group}
                                    isOver={overDroppableId === `gap-${rows[rowIndex - 1].group}`}
                                />
                            )}

                            <div className="cmd-grid-row">
                                {/* Step number column */}
                                <div className="cmd-grid-row-step">
                                    <span className="cmd-grid-step-num">#{rowIndex + 1}</span>
                                    {row.commands.length > 1 && (
                                        <span className="cmd-grid-parallel-badge" title="Commands in this row execute simultaneously">
                                            ∥
                                        </span>
                                    )}
                                    {row.isLocked && (
                                        <span className="cmd-grid-locked-badge" title="This step is always first or last">
                                            ⚑
                                        </span>
                                    )}
                                </div>

                                {/* Cards column */}
                                <DroppableRow
                                    group={row.group}
                                    isOver={overDroppableId === `row-${row.group}`}
                                    isLocked={row.isLocked}
                                >
                                    {row.commands.map((cmd) => (
                                        <DraggableCommandCard
                                            key={cmd.key}
                                            command={cmd}
                                            isSelected={cmd.key === selectedCommandKey}
                                            isLocked={row.isLocked}
                                            dragDisabled={isMavLike}
                                            moduleConfigsByKey={moduleConfigsByKey}
                                            moduleCommandOptions={moduleCommandOptions}
                                            onSelect={() => onCommandSelect(cmd.key)}
                                            onRemove={() => onRemoveCommand(cmd.key)}
                                        />
                                    ))}

                                    {/* Add parallel button (only on non-locked, non-primary rows) */}
                                    {!isMavLike && !row.isLocked && (
                                        <button
                                            type="button"
                                            className="cmd-grid-add-parallel"
                                            title="Add a command that runs in parallel with this step"
                                            onClick={() => onAddToRow('do', row.group)}
                                        >
                                            <PlusOutlined style={{ fontSize: 10 }} />
                                            <span>parallel</span>
                                        </button>
                                    )}
                                </DroppableRow>
                            </div>
                        </React.Fragment>
                    ))}

                    {/* Gap after last non-locked row (before nav_continue if locked) */}
                    {!isMavLike && rows.length > 1 && rows[rows.length - 1].isLocked && (
                        <DroppableGap
                            afterGroup={rows[rows.length - 2].group}
                            isOver={overDroppableId === `gap-${rows[rows.length - 2].group}`}
                        />
                    )}
                </div>

                {/* Add new sequential step */}
                <div className="cmd-grid-add-row">
                    <Space wrap size={8}>
                        <Button size="small" icon={<PlusOutlined />} onClick={() => onAddStep('do')}>
                            Add DO step
                        </Button>
                        <Button size="small" icon={<PlusOutlined />} onClick={() => onAddStep('condition')}>
                            Add condition step
                        </Button>
                        {!isMavLike && (
                            <Button size="small" icon={<PlusOutlined />} onClick={() => onAddStep('custom')}>
                                Add custom step
                            </Button>
                        )}
                    </Space>
                </div>

                {/* Command detail form */}
                {selectedCommand ? (
                    <CommandDetailForm
                        command={selectedCommand}
                        commandProtocol={commandProtocol}
                        moduleConfigsByKey={moduleConfigsByKey}
                        moduleCommandOptions={moduleCommandOptions}
                        onChange={(updates) => onCommandChange(selectedCommand.key, updates)}
                        onRemove={() => onRemoveCommand(selectedCommand.key)}
                    />
                ) : (
                    <div className="cmd-grid-detail cmd-grid-detail-empty">
                        <Text type="secondary">Click a command card above to edit its settings.</Text>
                    </div>
                )}
            </div>

            {/* DragOverlay shows a ghost of the dragged card */}
            <DragOverlay>
                {draggingCommand ? (
                    <div className="cmd-grid-card cmd-grid-card-overlay">
                        <div className="cmd-grid-card-content">
                            <Space wrap size={4}>
                                <Tag
                                    color={draggingCommand.isPrimary ? 'blue' : draggingCommand.kind === 'do' ? 'orange' : 'default'}
                                    style={{ margin: 0 }}
                                >
                                    {draggingCommand.isPrimary ? 'Primary' : draggingCommand.kind.toUpperCase()}
                                </Tag>
                                <Tag style={{ margin: 0 }}>
                                    {getDraftCommandDisplayName(draggingCommand, moduleConfigsByKey, moduleCommandOptions)}
                                </Tag>
                            </Space>
                            <Text className="cmd-grid-card-label" strong>
                                {draggingCommand.label?.trim()
                                    || getDraftCommandDisplayName(draggingCommand, moduleConfigsByKey, moduleCommandOptions)}
                            </Text>
                        </div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

export default CommandExecutionGrid;
